import isPlainObject from 'lodash/isPlainObject';
import {
	DESCRIPTION_FIELD_TYPE,
	ENVIRONMENT_FIELD_TYPE,
	TEXT_AREA_CF_TYPE,
} from '@atlassian/jira-project-settings-apps-common/src/consts.tsx';
import type { FieldValidationInput, ValidationError } from '../../../../../types/validation.tsx';
import { POSITION_FIELD_DEFAULT_VALUE_INPUT } from '../../../constants.tsx';
import { MAX_ALLOWED_TEXT_LENGTH } from '../single-line-text/index.tsx';
import { hasDefaultValueProperty } from '../utils.tsx';
import { messages } from './messages.tsx';

const valid: ReadonlyArray<ValidationError> = [];

// This is the maximum allowed text length for the ADF document serialised as a JSON string.
// It is defined in the monolith in https://stash.atlassian.com/projects/JIRACLOUD/repos/jira/browse/jira-components/jira-core/src/main/resources/jpm.xml
export const MAX_ALLOWED_MULTILINE_LENGTH = 32767;

const tooLongDescriptionField: ReadonlyArray<ValidationError> = [
	{
		position: POSITION_FIELD_DEFAULT_VALUE_INPUT,
		messageDescriptor: messages.defaultValueTooLongErrorDescription,
		values: { MAX_ALLOWED_TEXT_LENGTH },
	},
];

const tooLongOtherFields: ReadonlyArray<ValidationError> = [
	{
		position: POSITION_FIELD_DEFAULT_VALUE_INPUT,
		messageDescriptor: messages.defaultValueTooLongErrorValue,
		values: { MAX_ALLOWED_TEXT_LENGTH },
	},
];

const supportedFieldTypes: ReadonlyArray<string> = [
	DESCRIPTION_FIELD_TYPE,
	TEXT_AREA_CF_TYPE,
	ENVIRONMENT_FIELD_TYPE,
];

export const validateMultilineFieldDefaultValue = ({
	field,
	isValidationForced,
}: FieldValidationInput): ReadonlyArray<ValidationError> => {
	if (!hasDefaultValueProperty(field)) {
		return valid;
	}
	const { defaultValue, type } = field;
	if (!supportedFieldTypes.includes(type) || !isPlainObject(defaultValue)) {
		return valid;
	}

	if (!isValidationForced) {
		// The JSON.stringify call below can take quite a long time.
		// So we don't want it to run on every keystroke!
		// This will delay the validation until it's forced which the platform code
		// does when it needs to, usually on save.
		return valid;
	}

	const value = JSON.stringify(defaultValue);

	if (value && value.length > MAX_ALLOWED_MULTILINE_LENGTH) {
		if (type === DESCRIPTION_FIELD_TYPE) {
			// Description system field has its own special error message
			return tooLongDescriptionField;
		}
		return tooLongOtherFields;
	}
	return valid;
};
